import {useCallback, useEffect, useRef, useState} from "react";
import {CardEntryProvider, PaymentType} from "../../types";
import ServiceClient from "../../utils/ServiceClient";
import {PayoutMethodView} from "./types";
import {applyPayoutMethodsPolicy} from "./payoutMethodsPolicy";
import {buildSafeWithdrawFlowErrorLog} from "./utils";

const payoffMethodsLoadErrorMessage = 'We could not load payout methods. Please try again.';
// SMG-4151: a PayGate token (auth) failure gets its own copy so it stops masquerading as a methods failure.
const payGateTokenErrorMessage = 'We could not connect to the withdrawal service. Please try again.';

type PayoutMethodsLoadResult = | { status: 'success', nextMethodType?: PaymentType } | { status: 'error' }
  | { status: 'stale' };

export const usePayoutMethods = (amountCents: number) => {
  const [methodRows, setMethodRows] = useState<PayoutMethodView[]>([]);
  const [selectableMethodRows, setSelectableMethodRows] = useState<PayoutMethodView[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  // Server-driven (SMG-4057 D6): which provider a NEW payout card must be entered with. Unset for
  // everyone unless paygate says otherwise, which keeps today's Finix / eProtect entry unchanged.
  const [cardEntryProvider, setCardEntryProvider] = useState<CardEntryProvider>();
  const requestIdRef = useRef(0);

  const reset = useCallback(() => {
    requestIdRef.current += 1;
    setMethodRows([]);
    setSelectableMethodRows([]);
    setCardEntryProvider(undefined);
    setIsLoading(false);
    setError('');
  }, []);

  // The card save can still refuse the provider for this user (region / rollout). From then on the
  // flow uses today's card entry, exactly as if the field had been absent; a reload re-asks the server.
  const clearCardEntryProvider = useCallback(() => setCardEntryProvider(undefined), []);

  const load = useCallback(async (): Promise<PayoutMethodsLoadResult> => {
    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    setError('');
    let failedCall = 'getPayGateToken';

    try {
      await ServiceClient.getPayGateToken();
      failedCall = 'getPayoffMethodsV2';
      const response = await ServiceClient.getPayoffMethodsV2(amountCents);
      if (requestId !== requestIdRef.current) return {status: 'stale'};

      const policy = applyPayoutMethodsPolicy(response, amountCents);
      setMethodRows(policy.methodRows);
      setSelectableMethodRows(policy.selectableMethodRows);
      setCardEntryProvider(response.cardEntryProvider === 'PAYSAFE' ? 'PAYSAFE' : undefined);
      setError(policy.blockingError);
      return {status: 'success', nextMethodType: policy.defaultMethodType};

    } catch (err) {
      if (requestId !== requestIdRef.current) return {status: 'stale'};

      console.log(`${failedCall} error`, buildSafeWithdrawFlowErrorLog(err));
      setMethodRows([]);
      setSelectableMethodRows([]);
      setCardEntryProvider(undefined);
      setError(failedCall === 'getPayGateToken' ? payGateTokenErrorMessage : payoffMethodsLoadErrorMessage);
      return {status: 'error'};

    } finally {
      if (requestId === requestIdRef.current) setIsLoading(false);
    }
  }, [amountCents]);

  useEffect(() => () => {
    requestIdRef.current += 1;
  }, []);

  return {
    methodRows, availableMethodRows: selectableMethodRows, isLoadingPayoffMethods: isLoading,
    payoffMethodsError: error, cardEntryProvider, clearCardEntryProvider, loadPayoffMethods: load,
    resetPayoffMethods: reset
  };
};
